import { apiClient } from "@/services/apiClient";

export type MpConnectionStatus =
  | "active"
  | "disconnected"
  | "error"
  | "not_connected";

export interface MpStatus {
  connected: boolean;
  status: MpConnectionStatus;
  connectedAt: string | null;
  lastSyncedAt: string | null;
  mpUserEmail: string | null;
  mpSiteId: string | null;
  lastError: string | null;
  autoEmailBuyer: boolean;
  unresolvedErrorCount: number;
}

export interface MpError {
  id: string;
  mpPaymentId: string;
  errorCode: string;
  errorMessage: string;
  attemptCount: number;
  lastAttemptAt: string;
  createdAt: string;
}

export interface UpdateMpSettingsPayload {
  autoEmailBuyer?: boolean;
}

export async function getMpStatus(): Promise<MpStatus> {
  const { data } = await apiClient.get<MpStatus>(
    "/integrations/mercadopago/status",
  );
  return data;
}

export async function updateMpSettings(
  payload: UpdateMpSettingsPayload,
): Promise<MpStatus> {
  const { data } = await apiClient.patch<MpStatus>(
    "/integrations/mercadopago/settings",
    payload,
  );
  return data;
}

export async function listMpErrors(): Promise<MpError[]> {
  const { data } = await apiClient.get<MpError[]>(
    "/integrations/mercadopago/errors",
  );
  return data;
}

export async function retryMpError(id: string): Promise<{
  retried: true;
  outcome: string;
  invoiceId: string | null;
  reason: string | null;
}> {
  const { data } = await apiClient.post<{
    retried: true;
    outcome: string;
    invoiceId: string | null;
    reason: string | null;
  }>(`/integrations/mercadopago/errors/${id}/retry`, {});
  return data;
}

export async function dismissMpError(id: string): Promise<{ dismissed: true }> {
  const { data } = await apiClient.post<{ dismissed: true }>(
    `/integrations/mercadopago/errors/${id}/dismiss`,
    {},
  );
  return data;
}

export async function startMpOAuth(): Promise<{ url: string }> {
  const { data } = await apiClient.get<{ url: string }>(
    "/integrations/mercadopago/authorize",
  );
  return data;
}

export async function disconnectMp(): Promise<{ disconnected: true }> {
  const { data } = await apiClient.post<{ disconnected: true }>(
    "/integrations/mercadopago/disconnect",
    {},
  );
  return data;
}
